import { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash2, Send, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import VoiceButton from "@/components/VoiceButton";

const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white";
const labelCls = "text-xs font-semibold text-slate-600 block mb-1";

export default function AskQuestionsDialog({ task, clients, events, onClose, reload }) {
  const [clientId, setClientId] = useState(task?.client_id || "");
  const [eventId, setEventId] = useState(task?.event_id || "");
  const [contactName, setContactName] = useState(task?.contact_name || "");
  const [contactEmail, setContactEmail] = useState(task?.contact_email || "");
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("We've got a couple of questions for you before we quote for this job.");
  const [questions, setQuestions] = useState([""]);
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const [sentLink, setSentLink] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/question-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clientId || contactEmail) return;
    const c = (clients || []).find((x) => x.id === clientId);
    const ct = (c?.contacts || []).find((x) => x.email) || (c?.contacts || [])[0];
    if (ct?.email || c?.email) {
      setContactEmail(ct?.email || c.email);
      if (!contactName && ct?.name) setContactName(ct.name);
    }
  }, [clients, clientId, contactEmail, contactName]);

  const clientEvents = useMemo(() => (events || [])
    .filter((e) => !clientId || e.client_id === clientId)
    .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || "")), [events, clientId]);

  const pickClient = (cid) => {
    setClientId(cid);
    setEventId("");
    const c = (clients || []).find((x) => x.id === cid);
    const ct = (c?.contacts || []).find((x) => x.email) || (c?.contacts || [])[0];
    setContactName(ct?.name || "");
    setContactEmail(ct?.email || c?.email || "");
  };

  const pickEvent = (eid) => {
    setEventId(eid);
    const ev = clientEvents.find((e) => e.id === eid);
    if (ev && !title) setTitle(`Questions about ${ev.name}`);
  };

  const setQ = (i, v) => setQuestions((qs) => qs.map((q, j) => (j === i ? v : q)));
  const delQ = (i) => setQuestions((qs) => qs.filter((_, j) => j !== i));

  const loadTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (t) setQuestions([...t.questions, ""]);
  };

  const saveTemplate = async () => {
    const name = window.prompt("Template name (e.g. Standard event questions):");
    if (!name?.trim()) return;
    try {
      await api.post("/question-templates", { name: name.trim(), questions: questions.filter((q) => q.trim()) });
      const { data } = await api.get("/question-templates");
      setTemplates(data);
      toast.success("Template saved — pick it from the dropdown next time");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/question-forms", {
        client_id: clientId, event_id: eventId, task_id: task?.id || "",
        contact_name: contactName, contact_email: contactEmail,
        title, comment, questions: questions.filter((q) => q.trim()),
        origin: window.location.origin,
      });
      setSentLink(data.link);
      toast.success("Questions emailed to the client");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sentLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", sentLink);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="askq-overlay">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 max-h-[88vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()} data-testid="askq-dialog">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="font-heading text-lg font-bold text-[#0F2547]">Ask the client questions</h2>
            <p className="text-xs text-slate-500">They get an email with a private link — their answers land back here in To-Do.</p>
          </div>
          <button onClick={onClose} data-testid="askq-close" aria-label="Close"
            className="h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-500 shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        {sentLink ? (
          <div className="space-y-3" data-testid="askq-sent">
            <p className="text-sm text-slate-600">Email sent. You can also copy the private link and send it by text/WhatsApp:</p>
            <div className="flex gap-2">
              <input readOnly value={sentLink} data-testid="askq-sent-link" className={`${inputCls} flex-1 text-xs`} onFocus={(e) => e.target.select()} />
              <button onClick={copy} data-testid="askq-copy-link"
                className="h-11 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 shrink-0">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button onClick={onClose} data-testid="askq-done"
              className="w-full h-11 rounded-lg bg-[#005EB8] text-white text-sm font-bold hover:bg-[#004a93] transition-colors">Done</button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <label className="block">
                <span className={labelCls}>Client</span>
                <select value={clientId} onChange={(e) => pickClient(e.target.value)} data-testid="askq-client-select" className={inputCls}>
                  <option value="">Pick a client…</option>
                  {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className={labelCls}>Their event on the calendar (optional)</span>
                <select value={eventId} onChange={(e) => pickEvent(e.target.value)} data-testid="askq-event-select" disabled={!clientId} className={inputCls}>
                  <option value="">{clientId ? (clientEvents.length ? "Pick an event…" : "No events on the calendar for them") : "Pick a client first"}</option>
                  {clientEvents.map((e) => <option key={e.id} value={e.id}>{e.name}{e.event_date ? ` — ${e.event_date}` : ""}</option>)}
                </select>
              </label>
              <label className="block">
                <span className={labelCls}>Who to send it to</span>
                <input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Contact name" data-testid="askq-contact-name" className={inputCls} />
              </label>
              <label className="block">
                <span className={labelCls}>Their email</span>
                <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="name@company.co.uk" data-testid="askq-contact-email" className={inputCls} />
              </label>
            </div>
            <label className="block">
              <span className={labelCls}>Form title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Questions about your fun day" data-testid="askq-title" className={inputCls} />
            </label>
            <label className="block">
              <span className={labelCls}>Message to go with it</span>
              <div className="flex gap-1.5 items-start">
                <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="askq-comment"
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                <div className="pt-1.5">
                  <VoiceButton testid="askq-comment-voice"
                    onTranscript={(t) => setComment((c) => (c ? c + " " : "") + t)} />
                </div>
              </div>
            </label>
            {templates.length > 0 && (
              <label className="block">
                <span className={labelCls}>Load a saved template</span>
                <select defaultValue="" onChange={(e) => loadTemplate(e.target.value)} data-testid="askq-template-select" className={inputCls}>
                  <option value="">Pick a template…</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.questions.length} questions)</option>)}
                </select>
              </label>
            )}
            <div>
              <span className={labelCls}>Your questions</span>
              <div className="space-y-2" data-testid="askq-questions">
                {questions.map((q, i) => (
                  <div key={i} className="flex gap-1.5 items-start">
                    <span className="text-xs font-bold text-slate-400 pt-3 w-5 shrink-0">{i + 1}.</span>
                    <textarea rows={2} value={q} onChange={(e) => setQ(i, e.target.value)} placeholder="Type a question… or tap the mic and say it" data-testid={`askq-question-${i}`}
                      className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                    <div className="pt-1.5 shrink-0">
                      <VoiceButton testid={`askq-voice-${i}`}
                        onTranscript={(t) => setQ(i, (questions[i] ? questions[i] + " " : "") + t)} />
                    </div>
                    <button onClick={() => delQ(i)} disabled={questions.length === 1} data-testid={`askq-del-question-${i}`} aria-label="Remove question"
                      className="h-9 w-9 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 disabled:opacity-30 mt-0.5 shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setQuestions((qs) => [...qs, ""])} data-testid="askq-add-question"
                  className="h-10 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> Add question
                </button>
                <button onClick={saveTemplate} disabled={!questions.some((q) => q.trim())} data-testid="askq-save-template"
                  className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-slate-400 text-xs font-bold disabled:opacity-40">
                  Save as template
                </button>
              </div>
            </div>
            <button onClick={send} disabled={sending || !clientId || !questions.some((q) => q.trim())} data-testid="askq-send-btn"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors">
              <Send className="h-4 w-4" /> {sending ? "Sending…" : "Email the questions"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
